// Shared helper functions for WHS public pages (QR Attendance, Incident, Toolbox Talks)

/**
 * Retrieve current GPS coordinates using the browser's Geolocation API.
 */
export const getCoordinates = (options = {}) => {
  return new Promise((resolve) => {
    if (typeof navigator === "undefined" || !navigator.geolocation) {
      resolve({ latitude: 0, longitude: 0, accuracy: 0 });
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy || 0,
        });
      },
      (error) => {
        // If high accuracy or timeout failed, attempt low accuracy fallback
        navigator.geolocation.getCurrentPosition(
          (pos) => {
            resolve({
              latitude: pos.coords.latitude,
              longitude: pos.coords.longitude,
              accuracy: pos.coords.accuracy || 0,
            });
          },
          (err2) => {
            resolve({ latitude: 0, longitude: 0, accuracy: 0, error: err2 || error });
          },
          { enableHighAccuracy: false, timeout: 8000 }
        );
      },
      { enableHighAccuracy: true, timeout: 10000, ...options }
    );
  });
};

/**
 * Safely parse date string into a Date object.
 */
export const parseDate = (dateStr) => {
  if (!dateStr) return null;
  try {
    if (dateStr instanceof Date) return dateStr;
    let formatted = dateStr;
    if (typeof dateStr === "string") {
      formatted = formatted.replace(" ", "T");
      if (!/(Z|[+-]\d{2}:\d{2})$/.test(formatted)) {
        formatted += "Z";
      }
    }
    const d = new Date(formatted);
    return isNaN(d.getTime()) ? null : d;
  } catch (e) {
    return null;
  }
};

/**
 * Returns today's date in YYYY-MM-DD format for date pickers/inputs.
 */
export const getTodayInputDate = () => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

/**
 * Returns the current time formatted.
 * If includeAmPm is true: "09:30 AM".
 * If includeAmPm is false: "09:30" (24h).
 */
export const getCurrentFormattedTime = (includeAmPm = true) => {
  const now = new Date();
  let hrs = now.getHours();
  const mins = String(now.getMinutes()).padStart(2, "0");
  if (!includeAmPm) {
    return `${String(hrs).padStart(2, "0")}:${mins}`;
  }
  const ampm = hrs >= 12 ? "PM" : "AM";
  hrs = hrs % 12;
  hrs = hrs ? hrs : 12;
  return `${String(hrs).padStart(2, "0")}:${mins} ${ampm}`;
};

/**
 * Strips HTML tags and entities for character count or validation.
 */
export const stripHtml = (htmlString) => {
  if (!htmlString) return "";
  return htmlString
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .trim();
};
